/**
 * Vite entry point — React 19 + React Router v7.
 *
 * This file replaces Next.js's automatic bootstrapping.  All providers that
 * were previously in app/layout.tsx are composed here.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// Register module-contributed registry entries BEFORE any component renders
// or router modules are imported. This is a side-effect import — keep first.
import "@/generated/register-modules";
import { RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider, MutationCache, keepPreviousData } from "@tanstack/react-query";
import { ThemeProvider } from "@kubdee/ui/shell/theme-provider";
import { TooltipProvider } from "@kubdee/ui/shadcn/tooltip";
import { Toaster, toast } from "sonner";
import { router } from "@/router";
import type { ApiError } from "@kubdee/sdk/api-client";
import "./app/globals.css";

// ---------------------------------------------------------------------------
// Query Client (same config as the former QueryProvider)
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      placeholderData: keepPreviousData,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      const apiError = error as ApiError;
      const message = apiError.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      toast.error(message);
    },
  }),
});

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

const root = document.getElementById("root");
if (!root) throw new Error("Root element #root not found");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system">
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster
            richColors
            position="bottom-right"
            toastOptions={{
              className: "font-sans!",
              style: { fontFamily: "inherit" },
            }}
            style={{ fontFamily: "inherit" }}
          />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
